// Imported first so page modules can override the shared primitives.
import ui from './ui.module.css';
import { NOTIFICATION_QUERY_PARAM, parseAppPath } from '@circles/convex';
import type { FunctionReturnType } from 'convex/server';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { api, convexClient } from '../lib/convex';
import { useDeviceTokens } from '../lib/tokens';
import { initial, isImageIcon, relativeTime, tint } from './format';
import { useCachedQueries } from '../lib/liveQuery';
import { useLauncherPage } from './hooks';
import { BellIcon, ChevronLeftIcon } from './icons';
import styles from './Inbox.module.css';

type InboxItem = FunctionReturnType<typeof api.notifications.inbox>[number];
type Row = InboxItem & { circleSlug: string; circleName: string; deviceToken: string };

/** The app's root from the item's href (`/c/<circleId>/<app>/…`), which carries the circle identifier. */
function appBase(row: Row): string {
  const circleId = parseAppPath(row.href)?.circleId ?? row.circleSlug;
  return `/c/${circleId}/${row.app.slug}/`;
}

/** Route "/inbox" — notifications from every circle on this device, newest first. */
export function Inbox() {
  useLauncherPage();
  const tokens = useDeviceTokens();
  const [opening, setOpening] = useState<string | null>(null);
  const slugs = Object.keys(tokens);

  const argsBySlug = useMemo(
    () => Object.fromEntries(Object.entries(tokens).map(([slug, e]) => [slug, { deviceToken: e.token }])),
    [tokens],
  );
  const inboxes = useCachedQueries(api.notifications.inbox, argsBySlug);
  const loading = slugs.some((s) => !inboxes[s]?.data && !inboxes[s]?.error);
  const live = slugs.every((s) => inboxes[s]?.error || (inboxes[s]?.data && !inboxes[s]?.stale));

  const rows: Row[] = slugs
    .flatMap((slug) =>
      (inboxes[slug]?.data ?? []).map((n) => ({
        ...n,
        circleSlug: slug,
        circleName: tokens[slug]!.circleName,
        deviceToken: tokens[slug]!.token,
      })),
    )
    .sort((a, b) => b.createdAt - a.createdAt);
  const unread = rows.filter((r) => r.readAt === undefined);
  const multiCircle = slugs.length > 1;

  async function open(row: Row) {
    setOpening(row._id);
    if (row.readAt === undefined) {
      try {
        await convexClient.mutation(api.notifications.markRead, { deviceToken: row.deviceToken, notificationId: row._id });
      } catch {
        // Opening the app matters more than the read flag.
      }
    }
    const url = new URL(row.href, window.location.origin);
    url.searchParams.set(NOTIFICATION_QUERY_PARAM, row._id);
    window.location.assign(url.pathname + url.search + url.hash);
  }

  function markAllRead() {
    const pending = new Set(unread.map((r) => r.deviceToken));
    for (const deviceToken of pending) {
      convexClient.mutation(api.notifications.markAllRead, { deviceToken }).catch(() => {});
    }
  }

  return (
    <main className={ui.page} data-ready={live ? 'true' : undefined}>
      <header className={styles.header}>
        <Link to="/" className={styles.back} aria-label="Back to your circles">
          <ChevronLeftIcon size={26} />
        </Link>
        <h1 className={styles.title}>Inbox</h1>
        <button className={styles.markAll} onClick={markAllRead} disabled={unread.length === 0}>
          Mark all read
        </button>
      </header>

      {loading && rows.length === 0 ? (
        <div className={styles.list} aria-busy="true" aria-label="Loading your inbox" data-testid="inbox-skeleton">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`${styles.item} ${styles.itemSkeleton}`} aria-hidden>
              <span className={`${ui.skeleton} ${styles.icon}`} />
              <span className={styles.text}>
                <span className={ui.skeleton} style={{ height: 15, width: '78%', borderRadius: 8 }} />
                <span className={ui.skeleton} style={{ height: 12, width: '40%', marginTop: 8, borderRadius: 8 }} />
              </span>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden>
            <BellIcon size={26} />
          </span>
          <p>Nothing yet. When someone sends you something or an app reminds you, it shows up here.</p>
          <Link to="/" className={`${ui.secondary} ${styles.emptyAction}`}>
            Go to your apps
          </Link>
        </div>
      ) : (
        <ul className={styles.list}>
          {rows.map((row) => {
            const isUnread = row.readAt === undefined;
            return (
              <li key={row._id}>
                <button
                  className={`${styles.item} ${isUnread ? styles.unread : ''}`}
                  onClick={() => void open(row)}
                  disabled={opening !== null}
                >
                  <span className={styles.icon} style={{ background: tint(row.app.slug) }}>
                    {row.app.icon && isImageIcon(row.app.icon) ? (
                      <img src={`${appBase(row)}${row.app.icon.replace(/^\.?\//, '')}`} alt="" />
                    ) : (
                      (row.app.icon ?? initial(row.app.name))
                    )}
                  </span>
                  <span className={styles.text}>
                    <span className={styles.message}>{row.message}</span>
                    <span className={styles.meta}>
                      {row.app.name}
                      {multiCircle ? ` in ${row.circleName}` : ''}
                    </span>
                  </span>
                  <span className={styles.aside}>
                    <span className={styles.time}>{relativeTime(row.createdAt)}</span>
                    {isUnread && <span className={styles.dot} aria-label="Unread" />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </main>
  );
}
